import { ref, toValue, toRaw } from 'vue'
import { useAuthStore } from '@/stores'
import { useRouter } from 'vue-router'

const baseUri = process.env.VUE_APP_API_URL
// Simple in-memory cache for GET requests
const requestCache = new Map()
// Automatically clear the entire cache every 5 minutes
const CACHE_CLEAR_INTERVAL = 1 * 60 * 1000
setInterval(() => requestCache.clear(), CACHE_CLEAR_INTERVAL)

// Token refresh state management to prevent race conditions
let isRefreshing = false
let refreshPromise = null

export function clearRequestCache() {
    requestCache.clear()
}

function invalidateEndpointCache(endpoint) {
    const prefix = `${baseUri}/${endpoint}`
    for (const key of requestCache.keys()) {
        if (key.startsWith(prefix)) {
            requestCache.delete(key)
        }
    }
}

export function useBackend(endpoint, path, options = {}) {
    const item = ref(null)
    const items = ref([])
    const error = ref(null)
    const totalItems = ref(0)
    const loading = ref(options.initialLoading ?? false)

    const router = useRouter()

    let abortController = null

    const cancelRequest = () => {
        if (abortController) {
            abortController.abort()
            abortController = null
        }
    }

    const getItems = async (params = {}) => {
        error.value = null
        cancelRequest()
        abortController = new AbortController()

        // Resolve refs to their raw values
        const resolvedParams = Object.keys(params).reduce((acc, key) => {
            acc[key] = toValue(params[key]);
            return acc;
        }, {});

        // Build an array of key-value pairs
        const pairs = Object.entries(resolvedParams).flatMap(([key, value]) =>
            Array.isArray(value) ? value.map(item => [key, item]) : [[key, value]]
        );

        // Construct query string from pairs
        const queryString = new URLSearchParams(pairs).toString();
        const urlWithParams = `${baseUri}/${endpoint}?${queryString}`;

        /*
        const cached = requestCache.get(urlWithParams)
        if (cached) {
            items.value = cached.data
            totalItems.value = cached.total
            return
        }
        */

        const response = await customFetch(urlWithParams, {
            method: 'GET',
            signal: abortController.signal
        });

        if (response) {
            items.value = response.data || response
            totalItems.value = response.totalRecords || items.value.length
            requestCache.set(urlWithParams, { data: items.value, total: totalItems.value })
        }
    
            }

    const getItem = async(id, params = {}) => {
        item.value = null
        error.value = null
        cancelRequest()
        abortController = new AbortController()

        // Resolve refs to their raw values
        const resolvedParams = Object.keys(params).reduce((acc, key) => {
            acc[key] = toValue(params[key]);
            return acc;
        }, {});
        
        // Construct query string from params object
        const queryString = new URLSearchParams(resolvedParams).toString();
        const urlWithParams = `${baseUri}/${endpoint}/${id}?${queryString}`;
        
        const response = await customFetch( urlWithParams, {
            method: 'GET',
            signal: abortController.signal
        });
        
        if (response) {
            item.value = response
        }

    }

    const downloadItem = async(id, params = {}) => {
        error.value = null
        cancelRequest()
        abortController = new AbortController()

        const resolvedParams = Object.keys(params).reduce((acc, key) => {
            acc[key] = toValue(params[key]);
            return acc;
        }, {});
        
        const queryString = new URLSearchParams(resolvedParams).toString();
        const urlWithParams = `${baseUri}/${endpoint}/${id}?${queryString}`;

        const response = await customFetch(urlWithParams, { method: 'GET', responseType: 'blob', signal: abortController.signal });

        if (response) {
            const blob = new Blob([response], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const contentDisposition = response.headers.get('content-disposition');
            let fileName = 'downloaded-file';
            
            if (contentDisposition) {
                const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/);
                if (match) {
                    fileName = decodeURIComponent(match[1] || match[2]);
                }
            }

            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        }
    }

    const addItem = async (data) => {
        error.value = null

        const response = await customFetch( `${baseUri}/${endpoint}`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: { 'Content-Type': 'application/json' }
        } )
        
        if (response) {
            item.value = response
            invalidateEndpointCache(endpoint)
            if( path )
            {
                router.push( `${path}/${item.value.id}`)
            }

        }
    }

    const addItemWithFile = async (data, file) => {
        error.value = null;
        const formData = new FormData();
    
        // Append files correctly
        if (Array.isArray(file)) {
            file.forEach(f => {
                formData.append("files", f);  // 'files' is the key expected by the backend
            });
        } else if (file) {
            formData.append("file", file);
        }
    
        // Append other data correctly, especially arrays
        for (const key in data) {
            if (Array.isArray(data[key])) {
                data[key].forEach(value => {
                    formData.append(`${key}`, value);
                });
            } else {
                formData.append(key, data[key]);
            }
        }
    
        const response = await customFetch(`${baseUri}/${endpoint}`, {
            method: 'POST',
            body: formData,
        });
    
        if (response) {
            item.value = response;
            invalidateEndpointCache(endpoint)
            if (path) {
                router.push(`${path}/${item.value.id}`);
            }
        }
    }

    const updateItem = async (id) => {
        
        error.value = null

        const rawData = toRaw(item.value);

        const response = await customFetch( `${baseUri}/${endpoint}/${id}`, { 
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
            },
            body:JSON.stringify(rawData) 
        } )
        
        if (response) {
            item.value = response
            invalidateEndpointCache(endpoint)
            if( path )
            {
                router.push( `${path}/${item.value.id}`)
            }
        }
    }

    const deleteItem = async (id) => {
        error.value = null
        const response = await customFetch( `${baseUri}/${endpoint}/${id}`, { method: 'DELETE' } )
        
        if (response) {
            invalidateEndpointCache(endpoint)
            if( path )
            {
                router.push( `${path}/${item.value.id}`)
            }
        }

    }

    async function customFetch(url, options = {}, retry = true) {
        const authStore = useAuthStore()
        loading.value = true
    
        const config = {
          ...options,
          credentials: 'include', // Important: send/receive HttpOnly cookies
          headers: {
            ...(options.headers || {})
          }
        }
        if(options.signal){
          config.signal = options.signal
        }
    
        // If NOT a GET request, include the CSRF token in a header
        if (config.method && config.method.toUpperCase() !== 'GET') {
          const token = authStore.csrfToken
          if (token) {
            config.headers['x-csrf-token'] = token
          }
        }
    
        try {
          const response = await fetch(url, config)
    
          if (!response.ok) {
            // 401 / 403 might happen if CSRF fails or session is invalid
            if (response.status === 401 && retry) {
              // Try to refresh token with race condition protection
              const refreshSuccess = await refreshToken()
              if (refreshSuccess) {
                // Retry the original request once
                return await customFetch(url, options, false)
              } else {
                // Refresh failed - always clear auth state to avoid stale data
                authStore.logout()
                // Only redirect if on a protected route
                const currentRoute = router.currentRoute.value
                if (currentRoute.meta.requiresAuth !== false) {
                  router.push('/auth/login')
                }
                return null
              }
            } else if (response.status === 401) {
              // Second 401 or retry=false - always clear auth state
              authStore.logout()
              // Only redirect if on a protected route
              const currentRoute = router.currentRoute.value
              if (currentRoute.meta.requiresAuth !== false) {
                router.push('/auth/login')
              }
              return null
            }
    
            // Attempt to parse error message
            let errorData = {}
            try {
              errorData = await response.json()
            } catch (e) {
              errorData = { message: response.statusText || 'An error occurred' }
            }
    
            error.value = {
              status: response.status,
              // To show endpoint in error message
              //message: errorData.message || `An error occurred - ${endpoint}`,
              message: errorData.message || `An error occurred`,
              errors: errorData.errors || null
            }
            return null
          } else {
            loading.value = false
    
            // Return early if 204 (No Content)
            if (response.status === 204) {
              return
            }
    
            if (options.responseType === 'blob') {
              const blob = await response.blob()
              // let the caller see the response headers
              blob.headers = response.headers
              return blob
            } else {
              return await response.json()
            }
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            return null
          }
          console.error('[backend] Request error:', err)
          error.value = { status: null, message: 'An unknown error occurred' }
          return null
        } finally {
          loading.value = false
        }
      }

      async function refreshToken() {
        const authStore = useAuthStore()

        // Race condition protection: if refresh is already in progress, wait for it
        if (isRefreshing) {
          return refreshPromise
        }

        // Mark refresh as in progress
        isRefreshing = true

        refreshPromise = (async () => {
          try {
            const res = await fetch(`${baseUri}/auth/refresh`, {
              method: 'POST',
              credentials: 'include'
            })

            if (!res.ok) {
              console.error(`Token refresh failed with status: ${res.status}`)
              return false
            }

            const data = await res.json()

            // Update CSRF token if provided (critical fix!)
            // Note: Pinia auto-unwraps refs, so we assign directly without .value
            if (data.csrfToken) {
              authStore.csrfToken = data.csrfToken
              localStorage.setItem('csrf_token', data.csrfToken)
            }

            return true
          } catch (error) {
            console.error('Token refresh error:', error)
            return false
          } finally {
            // Reset refresh state
            isRefreshing = false
            refreshPromise = null
          }
        })()

        return refreshPromise
      }

    // This will test RError Component globally
    //error.value = { status: null, message: `Testing RErrorsAlert Component - ${endpoint}`, errors: null }


    return {
        item,
        items,
        totalItems,
        error,
        downloadItem,
        getItems,
        getItem,
        addItem,
        addItemWithFile,
        updateItem,
        deleteItem,
        cancelRequest,
        loading
    }
}
